/**
 * XLSX / XLSM — reading and basic cell editing (§8 Phase 1, §15).
 *
 * Reading builds a cell model; **saving does not use it**. Edits are applied as
 * surgical replacements inside the one worksheet part that changed, and every
 * other part is copied in its original compressed form. A macro-enabled
 * workbook therefore keeps its `vbaProject.bin` bit-identical, and charts,
 * pivot caches and conditional formatting survive because nothing ever parses
 * or rewrites them.
 *
 * Two §15 rules shape the model:
 *
 * - Formulas are preserved along with their cached result. Modu Viewer does not
 *   have a calculation engine, so a formula it cannot recompute is shown with
 *   its last known value and labelled as such — never dropped.
 * - External data connections and web queries are never resolved, and macros
 *   are never executed, regardless of what the file asks for.
 */
import {
  FORMATS_BY_ID, formatFileSize,
} from '@moduviewer/core';
import type {
  ExportResult, FileSource, FormatHandler, HandlerContext,
  HandlerWarning, OpenedDocument, Preview, ValidationReport,
} from '../contract.js';
import { HandlerError, throwIfAborted } from '../contract.js';
import { openPackage, readPartText, repackPackage } from '../ooxml/package.js';
import type { OpcPackage } from '../ooxml/package.js';
import {
  applyCellEdits, findElement, formatRef, parseRef, readAttribute, unescapeXml,
} from './sheet-xml.js';
import type { CellValue } from './sheet-xml.js';

export interface XlsxCell {
  readonly ref: string;
  readonly row: number;
  readonly col: number;
  /** Display value. For a formula cell this is the cached result. */
  readonly text: string;
  readonly numeric: number | null;
  /** Original formula, kept even though we cannot recalculate it (§15). */
  readonly formula: string | null;
}

export interface XlsxSheet {
  readonly name: string;
  /** Part path, e.g. `xl/worksheets/sheet1.xml`. */
  readonly part: string;
  readonly cells: readonly XlsxCell[];
  readonly rowCount: number;
  readonly colCount: number;
}

export interface XlsxModel {
  readonly sheets: readonly XlsxSheet[];
  readonly activeSheet: number;
  /** Pending edits per part path, applied at export time. */
  readonly edits: ReadonlyMap<string, ReadonlyMap<string, CellValue>>;
  readonly hasMacros: boolean;
  readonly hasFormulas: boolean;
  readonly hasExternalLinks: boolean;
}

const PREVIEW_ROWS = 50;
const PREVIEW_COLS = 20;

/* ------------------------------------------------------------------ *
 * package structure
 * ------------------------------------------------------------------ */

interface SheetRef { readonly name: string; readonly part: string }

/** Resolve sheet names to worksheet part paths through the workbook rels. */
function resolveSheets(pkg: OpcPackage): SheetRef[] {
  const workbookXml = readPartText(pkg, 'xl/workbook.xml');
  const relsXml = pkg.entryByName.has('xl/_rels/workbook.xml.rels')
    ? readPartText(pkg, 'xl/_rels/workbook.xml.rels')
    : '';

  const relTargets = new Map<string, string>();
  for (const m of relsXml.matchAll(/<Relationship\b[^>]*\/?>/g)) {
    const tag = m[0];
    const id = /\bId="([^"]+)"/.exec(tag)?.[1];
    const target = /\bTarget="([^"]+)"/.exec(tag)?.[1];
    if (id && target) {
      // Targets are relative to the part's folder, and may be absolute.
      const normalized = target.startsWith('/') ? target.slice(1)
        : target.startsWith('xl/') ? target : `xl/${target.replace(/^\.\//, '')}`;
      relTargets.set(id, normalized);
    }
  }

  const sheets: SheetRef[] = [];
  for (const m of workbookXml.matchAll(/<sheet\b[^>]*\/?>/g)) {
    const tag = m[0];
    const name = /\bname="([^"]*)"/.exec(tag)?.[1];
    const rid = /\br:id="([^"]+)"/.exec(tag)?.[1] ?? /\bid="([^"]+)"/.exec(tag)?.[1];
    if (!name) continue;
    const part = rid ? relTargets.get(rid) : undefined;
    if (part && pkg.entryByName.has(part)) sheets.push({ name: unescapeXml(name), part });
  }

  // Some generators omit rels entirely; fall back to whatever worksheets exist.
  if (sheets.length === 0) {
    const parts = pkg.directory.entries
      .map((e) => e.name)
      .filter((n) => /^xl\/worksheets\/sheet\d+\.xml$/.test(n))
      .sort();
    parts.forEach((part, i) => sheets.push({ name: `Sheet${i + 1}`, part }));
  }

  return sheets;
}

/** Shared string table, resolved to plain text. */
function readSharedStrings(pkg: OpcPackage): string[] {
  if (!pkg.entryByName.has('xl/sharedStrings.xml')) return [];
  const xml = readPartText(pkg, 'xl/sharedStrings.xml');
  const out: string[] = [];
  for (const si of xml.matchAll(/<si\b[^>]*>([\s\S]*?)<\/si>/g)) {
    // A shared string can be split across several runs; concatenating the <t>
    // elements is what produces the text the user actually sees.
    const parts = [...si[1]!.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)].map((m) => unescapeXml(m[1]!));
    out.push(parts.join(''));
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * worksheet parsing
 * ------------------------------------------------------------------ */

function parseSheet(xml: string, sharedStrings: readonly string[], maxCells: number): {
  cells: XlsxCell[]; rowCount: number; colCount: number; truncated: boolean;
} {
  const cells: XlsxCell[] = [];
  let rowCount = 0;
  let colCount = 0;
  let truncated = false;

  for (const m of xml.matchAll(/<c\b([^>]*?)(\/>|>([\s\S]*?)<\/c>)/g)) {
    // Stopping is not the same as finishing: the caller has to know whether it
    // is looking at the whole sheet or only as much as the budget allowed.
    if (cells.length >= maxCells) { truncated = true; break; }
    const attrs = m[1]!;
    const body = m[3] ?? '';

    const ref = /\br="([^"]+)"/.exec(attrs)?.[1];
    if (!ref) continue;
    const parsed = parseRef(ref);
    if (!parsed) continue;

    const type = /\bt="([^"]+)"/.exec(attrs)?.[1] ?? 'n';
    const formula = /<f\b[^>]*>([\s\S]*?)<\/f>/.exec(body)?.[1];
    const rawValue = /<v\b[^>]*>([\s\S]*?)<\/v>/.exec(body)?.[1];

    let text = '';
    let numeric: number | null = null;

    if (type === 's') {
      const index = Number(rawValue);
      text = Number.isFinite(index) ? sharedStrings[index] ?? '' : '';
    } else if (type === 'inlineStr') {
      const runs = [...body.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)].map((t) => unescapeXml(t[1]!));
      text = runs.join('');
    } else if (type === 'b') {
      text = rawValue === '1' ? 'TRUE' : 'FALSE';
    } else if (type === 'e') {
      text = rawValue ?? '#ERROR';
    } else if (rawValue !== undefined) {
      const n = Number(rawValue);
      if (Number.isFinite(n)) { numeric = n; text = rawValue; }
      else text = unescapeXml(rawValue);
    }

    cells.push({
      ref: parsed.ref,
      row: parsed.row,
      col: parsed.col,
      text,
      numeric,
      formula: formula === undefined ? null : unescapeXml(formula),
    });
    rowCount = Math.max(rowCount, parsed.row + 1);
    colCount = Math.max(colCount, parsed.col + 1);
  }

  return { cells, rowCount, colCount, truncated };
}

/* ------------------------------------------------------------------ *
 * handler
 * ------------------------------------------------------------------ */

function detectExternalLinks(pkg: OpcPackage): boolean {
  return pkg.directory.entries.some((e) => e.name.startsWith('xl/externalLinks/'));
}

function detectMacros(pkg: OpcPackage): boolean {
  return pkg.directory.entries.some((e) => e.name.endsWith('vbaProject.bin'));
}

export const xlsxHandler: FormatHandler<XlsxModel> = {
  id: 'xlsx',
  formatIds: ['xlsx', 'xlsm'],

  async probe(source: FileSource, ctx: HandlerContext): Promise<Preview> {
    throwIfAborted(ctx.signal);
    const pkg = openPackage(source.bytes);
    const sheets = resolveSheets(pkg);
    if (sheets.length === 0) {
      throw new HandlerError('error.damagedOrIncomplete', 'workbook declares no worksheets');
    }

    // §11: parse only the first sheet, and only the top-left corner of it.
    const shared = readSharedStrings(pkg);
    const first = sheets[0]!;
    const parsed = parseSheet(readPartText(pkg, first.part), shared, PREVIEW_ROWS * PREVIEW_COLS);

    const grid: string[][] = [];
    for (const cell of parsed.cells) {
      if (cell.row >= PREVIEW_ROWS || cell.col >= PREVIEW_COLS) continue;
      (grid[cell.row] ??= [])[cell.col] = cell.text;
    }

    return {
      kind: 'sheets',
      unitCount: sheets.length,
      unitKey: 'viewer.sheet',
      firstContent: {
        sheetNames: sheets.map((s) => s.name),
        rows: grid.map((r) => Array.from(r, (v) => v ?? '')),
      },
      warnings: [],
    };
  },

  async open(source: FileSource, ctx: HandlerContext): Promise<OpenedDocument<XlsxModel>> {
    throwIfAborted(ctx.signal);
    const pkg = openPackage(source.bytes);
    const sheetRefs = resolveSheets(pkg);
    if (sheetRefs.length === 0) {
      throw new HandlerError('error.damagedOrIncomplete', 'workbook declares no worksheets');
    }

    const shared = readSharedStrings(pkg);
    const sheets: XlsxSheet[] = [];
    let totalCells = 0;
    let hasFormulas = false;

    for (const ref of sheetRefs) {
      throwIfAborted(ctx.signal);
      const remaining = ctx.limits.maxSpreadsheetCells - totalCells;
      const parsed = remaining > 0
        ? parseSheet(readPartText(pkg, ref.part), shared, remaining)
        : { cells: [], rowCount: 0, colCount: 0, truncated: true };
      // Refuse rather than silently show a partial workbook. A spreadsheet cut
      // off at an arbitrary cell looks like data loss to the person reading it,
      // and §19 forbids presenting partial support as if it were complete.
      if (parsed.truncated) {
        throw new HandlerError(
          'error.unableToRead',
          `workbook exceeds the cell budget of ${ctx.limits.maxSpreadsheetCells}`,
        );
      }
      totalCells += parsed.cells.length;
      if (parsed.cells.some((c) => c.formula !== null)) hasFormulas = true;
      sheets.push({
        name: ref.name, part: ref.part,
        cells: parsed.cells, rowCount: parsed.rowCount, colCount: parsed.colCount,
      });
    }

    const hasMacros = detectMacros(pkg);
    const hasExternalLinks = detectExternalLinks(pkg);
    const spec = FORMATS_BY_ID.get(source.formatId);

    const warnings: HandlerWarning[] = [];
    if (hasMacros) {
      warnings.push({
        messageKey: 'detect.warning.macrosBlocked',
        severity: 'caution',
        detail: 'vbaProject.bin is preserved byte-for-byte on save and never executed (§15, §30).',
      });
    }
    if (hasFormulas) {
      warnings.push({
        messageKey: 'error.formulaNotRecalculated',
        severity: 'info',
        detail: 'Formulas are shown with their last saved result. Modu Viewer does not recalculate them.',
      });
    }
    if (hasExternalLinks) {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'caution',
        detail: 'This workbook links to external data. Those links are never resolved (§15, §30).',
      });
    }
    if (source.sizeBytes > 20 * 1024 * 1024) {
      warnings.push({
        messageKey: 'status.loadingRemaining',
        severity: 'info',
        detail: `large workbook (${formatFileSize('en', source.sizeBytes)})`,
      });
    }

    return {
      formatId: source.formatId,
      model: {
        sheets,
        activeSheet: 0,
        edits: new Map(),
        hasMacros,
        hasFormulas,
        hasExternalLinks,
      },
      readOnly: ctx.assessment.forceReadOnly,
      editLevel: spec?.edit ?? 'basic-edit',
      lossOnSave: spec?.lossOnSave ?? [],
      warnings,
    };
  },

  async exportBytes(
    model: XlsxModel, source: FileSource, ctx: HandlerContext,
  ): Promise<ExportResult> {
    throwIfAborted(ctx.signal);
    const pkg = openPackage(source.bytes);
    const replacements = new Map<string, Uint8Array>();
    const dropped: string[] = [];

    for (const [part, cellEdits] of model.edits) {
      if (cellEdits.size === 0) continue;
      const xml = readPartText(pkg, part);
      const result = applyCellEdits(
        xml,
        [...cellEdits].map(([ref, value]) => ({ ref, value })),
      );
      if (result.failed.length > 0) {
        throw new HandlerError(
          'error.unableToRead',
          `could not place cells: ${result.failed.join(', ')}`,
        );
      }
      for (const ref of result.formulasReplaced) {
        dropped.push(`${part} ${ref}: 수식이 입력값으로 대체됨`);
      }
      replacements.set(part, new TextEncoder().encode(result.xml));
    }

    const repacked = repackPackage(pkg, replacements);

    return {
      bytes: repacked.bytes,
      suggestedName: source.filename,
      formatId: source.formatId,
      dropped,
    };
  },

  async validateExport(
    bytes: Uint8Array, model: XlsxModel, ctx: HandlerContext,
  ): Promise<ValidationReport> {
    throwIfAborted(ctx.signal);
    const issues: string[] = [];
    let reopened = false;
    let contentPreserved = false;

    try {
      const pkg = openPackage(bytes);
      const sheets = resolveSheets(pkg);
      reopened = true;

      if (sheets.length !== model.sheets.length) {
        issues.push(`sheet count changed: ${model.sheets.length} → ${sheets.length}`);
      }

      // §15/§30 — the macro payload must still be there, untouched.
      if (model.hasMacros && !detectMacros(pkg)) {
        issues.push('macro storage was lost during save');
      }

      const shared = readSharedStrings(pkg);
      for (const expected of model.sheets) {
        const actual = sheets.find((s) => s.part === expected.part);
        if (!actual) { issues.push(`worksheet part missing: ${expected.part}`); continue; }

        const parsed = parseSheet(readPartText(pkg, expected.part), shared, ctx.limits.maxSpreadsheetCells);
        const edits = model.edits.get(expected.part);

        for (const cell of expected.cells) {
          const edited = edits?.get(cell.ref);
          const found = parsed.cells.find((c) => c.ref === cell.ref);
          if (!found) {
            if (!edited) issues.push(`cell ${cell.ref} disappeared from ${expected.part}`);
            continue;
          }
          if (edited) continue; // its value is expected to differ
          if (found.text !== cell.text) {
            issues.push(`cell ${cell.ref} changed: "${cell.text}" → "${found.text}"`);
          }
          if (found.formula !== cell.formula) {
            issues.push(`formula lost at ${cell.ref}`);
          }
        }
      }

      contentPreserved = issues.length === 0;
    } catch (err) {
      issues.push(`could not reopen the exported workbook: ${(err as Error).message}`);
    }

    return { ok: reopened && contentPreserved, reopened, contentPreserved, issues };
  },
};

/* ------------------------------------------------------------------ *
 * editing helpers
 * ------------------------------------------------------------------ */

/** Queue a cell edit. Nothing is written until export (§13). */
export function setCell(
  model: XlsxModel, sheetIndex: number, ref: string, value: CellValue,
): XlsxModel {
  const sheet = model.sheets[sheetIndex];
  if (!sheet) throw new HandlerError('error.unableToRead', `no sheet at index ${sheetIndex}`);
  const parsed = parseRef(ref);
  if (!parsed) throw new HandlerError('error.unableToRead', `invalid cell reference: ${ref}`);

  const edits = new Map(model.edits);
  const forPart = new Map(edits.get(sheet.part) ?? []);
  forPart.set(parsed.ref, value);
  edits.set(sheet.part, forPart);

  // Reflect the edit in the readable model so the UI shows it immediately.
  const text = value.kind === 'text' ? value.text
    : value.kind === 'number' ? String(value.value)
      : value.kind === 'boolean' ? (value.value ? 'TRUE' : 'FALSE') : '';
  const numeric = value.kind === 'number' ? value.value : null;

  const existingIndex = sheet.cells.findIndex((c) => c.ref === parsed.ref);
  const updatedCell: XlsxCell = {
    ref: parsed.ref, row: parsed.row, col: parsed.col, text, numeric, formula: null,
  };
  const cells = existingIndex >= 0
    ? sheet.cells.map((c, i) => (i === existingIndex ? updatedCell : c))
    : [...sheet.cells, updatedCell];

  const sheets = model.sheets.map((s, i) => (i === sheetIndex ? {
    ...s,
    cells,
    rowCount: Math.max(s.rowCount, parsed.row + 1),
    colCount: Math.max(s.colCount, parsed.col + 1),
  } : s));

  return { ...model, sheets, edits };
}

/** Cell lookup by reference. */
export function getCell(sheet: XlsxSheet, ref: string): XlsxCell | null {
  const parsed = parseRef(ref);
  if (!parsed) return null;
  return sheet.cells.find((c) => c.ref === parsed.ref) ?? null;
}

/** Dense grid for rendering, with empty strings where a cell is absent. */
export function toGrid(sheet: XlsxSheet): string[][] {
  const grid: string[][] = Array.from({ length: sheet.rowCount }, () => Array.from({ length: sheet.colCount }, () => ''));
  for (const cell of sheet.cells) {
    const row = grid[cell.row];
    if (row) row[cell.col] = cell.text;
  }
  return grid;
}

export { formatRef, parseRef, findElement, readAttribute };
export type { CellValue };
